import { useCallback, useEffect, useRef, useState } from "react";
import { useLoadContext } from "../context/loadProvider";

/**
 * Carga datos de un servicio y expone su estado.
 *
 * `global` decide si se muestra el preloader a pantalla completa. Por defecto
 * solo se usa en la primera carga: las recargas por filtro/paginación mantienen
 * el contenido en pantalla y exponen `loading` para un skeleton local, evitando
 * que el layout parpadee en cada tecleo.
 */
export const useFetch = (fetchFunction, { params = {}, global = false, skip = false } = {}) => {
    const [refres, setRefresh] = useState(0);
    const [data, setData] = useState(null);
    const [pagination, setPagination] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(!skip);
    const { setLoad } = useLoadContext();

    const paramsKey = JSON.stringify(params);
    const paramsRef = useRef(params);
    paramsRef.current = params;

    const hasLoadedOnce = useRef(false);

    const refresh = useCallback(() => setRefresh((value) => value + 1), []);

    useEffect(() => {
        if (skip) {
            setLoading(false);
            return undefined;
        }

        // Evita que una respuesta lenta pise a una más reciente.
        let active = true;
        const showGlobalLoader = global && !hasLoadedOnce.current;

        const fetchData = async () => {
            try {
                setLoading(true);
                if (showGlobalLoader) setLoad(true);

                const result = await fetchFunction(paramsRef.current);
                if (!active) return;

                setError(null);
                if (result && result.pagination) {
                    setData(result.data);
                    setPagination(result.pagination);
                } else if (result && result.data) {
                    setData(result.data);
                    setPagination(null);
                } else {
                    setData(result);
                    setPagination(null);
                }
            } catch (err) {
                if (active) setError(err.message);
            } finally {
                if (active) {
                    hasLoadedOnce.current = true;
                    setLoading(false);
                }
                if (showGlobalLoader) setLoad(false);
            }
        };

        fetchData();

        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refres, paramsKey, skip]);

    return { data, pagination, error, loading, setData, setRefresh, refresh };
}
